import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Filter, X, ChevronDown } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { getProducts, getCategories } from '../api/api';
import ProductCard from '../components/ProductCard';

const ShopPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useStore();
  const t = translations[language];

  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products for dynamic filters
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sortBy, setSortBy] = useState('newest');
  
  // Dynamic filter options based on existing products
  const [availableColors, setAvailableColors] = useState([]);
  const [priceMinMax, setPriceMinMax] = useState({ min: 0, max: 500 });
  const [dynamicCategories, setDynamicCategories] = useState([]);

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  
  // Color name mapping for display
  const colorNameMapping = {
    'noir': { fr: 'Noir', nl: 'Zwart' },
    'blanc': { fr: 'Blanc', nl: 'Wit' },
    'beige': { fr: 'Beige', nl: 'Beige' },
    'bordeaux': { fr: 'Bordeaux', nl: 'Bordeaux' },
    'bleu marine': { fr: 'Bleu Marine', nl: 'Marineblauw' },
    'bleu': { fr: 'Bleu', nl: 'Blauw' },
    'vert': { fr: 'Vert', nl: 'Groen' },
    'vert forêt': { fr: 'Vert Forêt', nl: 'Bosgroen' },
    'gris': { fr: 'Gris', nl: 'Grijs' },
    'marron': { fr: 'Marron', nl: 'Bruin' },
    'rouge': { fr: 'Rouge', nl: 'Rood' },
    'rose': { fr: 'Rose', nl: 'Roze' },
    'orange': { fr: 'Orange', nl: 'Oranje' },
    'jaune': { fr: 'Jaune', nl: 'Geel' },
    'violet': { fr: 'Violet', nl: 'Paars' },
    'kaki': { fr: 'Kaki', nl: 'Kaki' },
    'navy': { fr: 'Navy', nl: 'Navy' },
	'aqua blue': { fr: 'Aqua Blue', nl: 'Turkoois' },
	'garnet': { fr: 'Garnet', nl: 'Granaat' },
    'crème': { fr: 'Crème', nl: 'Crème' },
  };
  
  // Color hex mapping
  const colorHexMapping = {
    'noir': '#000000',
    'blanc': '#FFFFFF',
    'beige': '#D4C4A8',
    'bordeaux': '#722F37',
    'bleu marine': '#000080',
    'bleu': '#4169E1',
    'vert': '#228B22',
    'vert forêt': '#228B22',
    'gris': '#9E9E9E',
    'marron': '#8B4513',
    'rouge': '#DC2626',
    'rose': '#EC4899',
    'orange': '#F97316',
    'jaune': '#EAB308',
    'violet': '#8B5CF6',
    'kaki': '#BDB76B',
    'navy': '#000080',
	'aqua blue': '#00FFFF',
	'garnet': '#733635',
    'crème': '#FFFDD0',
  };

  // Default category titles (fallback)
  const defaultCategoryTitles = {
    sweaters: t.nav.sweaters,
    hoodies: t.nav.hoodies,
    tshirts: t.nav.tshirts,
    jackets: t.nav.jackets,
  };

  // Get category name from dynamic categories or fallback to defaults/slug
  const getCategoryName = (slug) => {
    // First try dynamic categories from API
    const dynamicCat = dynamicCategories.find(c => c.slug === slug);
    if (dynamicCat) {
      return dynamicCat.translations?.[language]?.name || dynamicCat.translations?.fr?.name || slug;
    }
    // Fallback to default translations
    if (defaultCategoryTitles[slug]) {
      return defaultCategoryTitles[slug];
    }
    // Last resort: return the slug itself (capitalized)
    return slug.charAt(0).toUpperCase() + slug.slice(1);
  };

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories(true); // Only active categories
        if (data && Array.isArray(data)) {
          // Filter out legacy categories and store the full category objects
          const cats = data.filter(cat => !cat.legacy && cat.slug);
          setDynamicCategories(cats);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, [language]);

  // Load all products first to extract dynamic filter values
  useEffect(() => {
    const loadAllProducts = async () => {
      try {
        const params = {};
        if (category) params.category = category;
        const data = await getProducts(params);
        setAllProducts(data);
        
        // Extract unique colors from products
        const colorsSet = new Set();
        data.forEach(product => {
          product.variants?.forEach(variant => {
            if (variant.color && variant.stock > 0) {
              colorsSet.add(variant.color.toLowerCase());
            }
          });
        });
        
        // Build available colors with hex and localized names
        const colors = Array.from(colorsSet).map(colorKey => {
          const hex = colorHexMapping[colorKey] || '#9E9E9E';
          const names = colorNameMapping[colorKey] || { fr: colorKey, nl: colorKey };
          return {
            key: colorKey,
            name: language === 'fr' ? names.fr : names.nl,
            hex: hex
          };
        }).sort((a, b) => a.name.localeCompare(b.name));
        
        setAvailableColors(colors);
        
        // Extract price range from products
        if (data.length > 0) {
          const prices = data.map(p => p.price);
          const minPrice = Math.floor(Math.min(...prices));
          const maxPrice = Math.ceil(Math.max(...prices));
          setPriceMinMax({ min: minPrice, max: maxPrice });
          setPriceRange([minPrice, maxPrice]);
        }
      } catch (error) {
        console.error('Error loading products for filters:', error);
      }
    };
    loadAllProducts();
  }, [category, language]);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category) params.category = category;
        if (priceRange[0] > priceMinMax.min) params.min_price = priceRange[0];
        if (priceRange[1] < priceMinMax.max) params.max_price = priceRange[1];
        
        let data = await getProducts(params);

        // Client-side filtering for sizes and colors
        if (selectedSizes.length > 0) {
          data = data.filter((p) =>
            p.variants?.some(
              (v) => selectedSizes.includes(v.size) && v.stock > 0
            )
          );
        }

        if (selectedColors.length > 0) {
          data = data.filter((p) =>
            p.variants?.some(
              (v) =>
                selectedColors.some((c) => c.toLowerCase() === v.color?.toLowerCase()) &&
                v.stock > 0
            )
          );
        }

        // Sorting
        switch (sortBy) {
          case 'price-low':
            data.sort((a, b) => a.price - b.price);
            break;
          case 'price-high':
            data.sort((a, b) => b.price - a.price);
            break;
          case 'newest':
          default:
            data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }

        setProducts(data);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [category, selectedSizes, selectedColors, priceRange, sortBy, priceMinMax]);

  const toggleSize = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const toggleColor = (colorKey) => {
    setSelectedColors((prev) =>
      prev.includes(colorKey)
        ? prev.filter((c) => c !== colorKey)
        : [...prev, colorKey]
    );
  };

  const clearFilters = () => {
    setSelectedSizes([]);
    setSelectedColors([]);
    setPriceRange([priceMinMax.min, priceMinMax.max]);
    setSortBy('newest');
  };

  const hasFilters =
    selectedSizes.length > 0 ||
    selectedColors.length > 0 ||
    priceRange[0] > priceMinMax.min ||
    priceRange[1] < priceMinMax.max;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shop-page">
      {/* Header */}
      <div className="bg-amber-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-amber-200 text-sm mb-4">
            <Link to="/" className="hover:text-white">
              {t.nav.home}
            </Link>
            <span className="mx-2">/</span>
            <Link to="/shop" className="hover:text-white">
              {t.nav.shop}
            </Link>
            {category && (
              <>
                <span className="mx-2">/</span>
                <span className="text-white">{getCategoryName(category)}</span>
              </>
            )}
          </nav>
          <h1 className="text-4xl font-bold text-white">
            {category ? getCategoryName(category) : t.products.allProducts}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-gray-900">{t.products.filterBy}</h3>
                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-amber-700 hover:text-amber-800"
                  >
                    {t.products.clearFilters}
                  </button>
                )}
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">
                  {language === 'fr' ? 'Catégories' : 'Categorieën'}
                </h4>
                <div className="space-y-2">
                  <Link
                    to="/shop"
                    className={`block text-sm ${
                      !category ? 'text-amber-700 font-medium' : 'text-gray-600 hover:text-amber-700'
                    }`}
                  >
                    {t.products.allProducts}
                  </Link>
                  {dynamicCategories.length > 0 ? (
                    dynamicCategories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/shop/${cat.slug}`}
                        className={`block text-sm ${
                          category === cat.slug
                            ? 'text-amber-700 font-medium'
                            : 'text-gray-600 hover:text-amber-700'
                        }`}
                      >
                        {cat.translations?.[language]?.name || cat.translations?.fr?.name || cat.slug}
                      </Link>
                    ))
                  ) : (
                    // Fallback to default categories if no dynamic categories loaded
                    Object.keys(defaultCategoryTitles).map((cat) => (
                      <Link
                        key={cat}
                        to={`/shop/${cat}`}
                        className={`block text-sm ${
                          category === cat
                            ? 'text-amber-700 font-medium'
                            : 'text-gray-600 hover:text-amber-700'
                        }`}
                      >
                        {defaultCategoryTitles[cat]}
                      </Link>
                    ))
                  )}
                </div>
              </div>

              {/* Sizes */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">{t.products.size}</h4>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => toggleSize(size)}
                      className={`px-3 py-1 text-sm rounded border transition-colors ${
                        selectedSizes.includes(size)
                          ? 'bg-amber-700 text-white border-amber-700'
                          : 'border-gray-300 text-gray-600 hover:border-amber-700'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">{t.products.color}</h4>
                <div className="flex flex-wrap gap-2">
                  {availableColors.length > 0 ? (
                    availableColors.map((color) => (
                      <button
                        key={color.key}
                        onClick={() => toggleColor(color.key)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          selectedColors.includes(color.key)
                            ? 'border-amber-700 scale-110'
                            : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Aucune couleur disponible' : 'Geen kleuren beschikbaar'}</p>
                  )}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">{t.products.priceRange}</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                    className="w-20 px-2 py-1 border rounded text-sm"
                    min={priceMinMax.min}
                    max={priceRange[1]}
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="w-20 px-2 py-1 border rounded text-sm"
                    min={priceRange[0]}
                    max={priceMinMax.max}
                  />
                  <span className="text-gray-500">€</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {language === 'fr' ? `De ${priceMinMax.min}€ à ${priceMinMax.max}€` : `Van €${priceMinMax.min} tot €${priceMinMax.max}`}
                </p>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Mobile Filter Button & Sort */}
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => setShowFilters(true)}
                className="lg:hidden flex items-center gap-2 text-gray-700"
              >
                <Filter className="w-5 h-5" />
                {t.products.filterBy}
                {hasFilters && (
                  <span className="bg-amber-700 text-white text-xs px-2 py-0.5 rounded-full">
                    {selectedSizes.length + selectedColors.length}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t.products.sortBy}:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="newest">
                    {language === 'fr' ? 'Plus récents' : 'Nieuwste'}
                  </option>
                  <option value="price-low">
                    {language === 'fr' ? 'Prix croissant' : 'Prijs oplopend'}
                  </option>
                  <option value="price-high">
                    {language === 'fr' ? 'Prix décroissant' : 'Prijs aflopend'}
                  </option>
                </select>
              </div>
            </div>

            {/* Results count */}
            <p className="text-sm text-gray-500 mb-4">
              {products.length} {language === 'fr' ? 'produit(s)' : 'product(en)'}
            </p>

            {/* Products */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow animate-pulse">
                    <div className="aspect-[3/4] bg-gray-200" />
                    <div className="p-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-500">{t.products.noProducts}</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 text-amber-700 hover:text-amber-800"
                >
                  {t.products.clearFilters}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-80 bg-white overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">{t.products.filterBy}</h3>
              <button onClick={() => setShowFilters(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4">
              {/* Same filters as sidebar */}
              {/* Sizes */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">{t.products.size}</h4>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => toggleSize(size)}
                      className={`px-3 py-1 text-sm rounded border transition-colors ${
                        selectedSizes.includes(size)
                          ? 'bg-amber-700 text-white border-amber-700'
                          : 'border-gray-300 text-gray-600'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">{t.products.color}</h4>
                <div className="flex flex-wrap gap-2">
                  {availableColors.length > 0 ? (
                    availableColors.map((color) => (
                      <button
                        key={color.key}
                        onClick={() => toggleColor(color.key)}
                        className={`w-8 h-8 rounded-full border-2 ${
                          selectedColors.includes(color.key)
                            ? 'border-amber-700'
                            : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">{language === 'fr' ? 'Aucune couleur' : 'Geen kleuren'}</p>
                  )}
                </div>
              </div>

              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="w-full py-2 text-amber-700 border border-amber-700 rounded"
                >
                  {t.products.clearFilters}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;
